import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { StudySession, PodcastResponse, studySessionsApi, aiApi, documentsApi, transcriptionApi, authApi } from '../../../services/api';
import { shareApi } from '../../../services/shareApi';
import { SpaceCard } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { ArrowLeft, ArrowRight, FileText, RefreshCw, Play, Trash2, Download, Pause, ChevronDown, Share2 } from 'lucide-react';
import { FlowStep } from '../NewStudySession';
import StudyMaterialRenderer from '../../StudyMaterialRenderer';
import { MixedText } from '../../../utils/rtl';

const PODCAST_DEFAULT_VOICE_ID = 'mentor_warm';

// Custom Podcast Player Component
const CustomPodcastPlayer: React.FC<{
  audioUrl: string;
  isRTL: boolean;
}> = ({ audioUrl, isRTL }) => {
  const { t } = useTranslation();
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [showSpeedDropdown, setShowSpeedDropdown] = useState(false);

  const speedOptions = [1, 1.2, 1.5, 2];

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => !isDragging && setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);
    const handleEnded = () => setIsPlaying(false);

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [isDragging]);

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    if (!audio) return;

    const progressBar = e.currentTarget;
    const rect = progressBar.getBoundingClientRect();

    // Calculate position based on RTL mode
    const clickX = isRTL ? rect.right - e.clientX : e.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newTime = percentage * duration;

    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleProgressDrag = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging) return;

    const audio = audioRef.current;
    if (!audio) return;

    const progressBar = e.currentTarget;
    const rect = progressBar.getBoundingClientRect();

    // Calculate position based on RTL mode
    const clickX = isRTL ? rect.right - e.clientX : e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, clickX / rect.width));
    const newTime = percentage * duration;

    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleMouseDown = () => {
    setIsDragging(true);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const changeSpeed = (rate: number) => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.playbackRate = rate;
    setPlaybackRate(rate);
    setShowSpeedDropdown(false);
  };

  const formatTime = (seconds: number) => {
    if (!isFinite(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="w-full rounded-3xl border border-vibrant-orange/20 bg-white/95 p-4 sm:p-5 shadow-[0_18px_32px_rgba(255,138,76,0.08)]">
      <audio ref={audioRef} src={audioUrl} preload="metadata" />

      <div className="flex flex-col gap-4 sm:gap-5">
        <div className={`flex flex-col sm:flex-row items-stretch gap-5 sm:gap-6 ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
          <button
            onClick={togglePlayPause}
            className="flex-shrink-0 w-16 h-16 rounded-full bg-gradient-to-br from-vibrant-orange to-vibrant-orange/85
                     hover:from-vibrant-orange/90 hover:to-vibrant-orange
                     shadow-[0_12px_24px_rgba(255,138,76,0.28)]
                     flex items-center justify-center
                     transition-all duration-200 hover:scale-105 active:scale-95 self-center sm:self-auto"
          >
            {isPlaying ? (
              <Pause className="w-6 h-6 text-white" fill="white" />
            ) : (
              <Play className={`w-6 h-6 text-white ${isRTL ? 'mr-1 scale-x-[-1]' : 'ml-1'}`} fill="white" />
            )}
          </button>

          <div className="flex-1 flex flex-col gap-3 w-full">
            <div
              className="relative h-2.5 bg-surface-muted/20 rounded-full cursor-pointer border border-vibrant-orange/30"
              onClick={handleProgressClick}
              onMouseMove={handleProgressDrag}
              onMouseDown={handleMouseDown}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              <div className="absolute inset-0 rounded-full bg-white/70" />
              <div
                className={`absolute inset-y-0 ${isRTL ? 'right-0 bg-gradient-to-l' : 'left-0 bg-gradient-to-r'} from-vibrant-orange to-[#ff8c4c] rounded-full transition-all`}
                style={{ width: `${progress}%` }}
              />
              <div
                className="absolute top-1/2 -translate-y-1/2 w-5 h-5 bg-white rounded-full shadow-[0_4px_12px_rgba(255,138,76,0.35)]
                           border border-vibrant-orange/60 transition-transform duration-100 cursor-grab active:cursor-grabbing
                           hover:scale-110"
                style={isRTL ? { right: `calc(${progress}% - 10px)` } : { left: `calc(${progress}% - 10px)` }}
              />
            </div>

            <div className={`flex justify-between text-sm font-medium text-surface-secondary ${isRTL ? 'flex-row-reverse' : ''}`}>
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>

          <div className="relative self-center sm:self-auto">
            <button
              onClick={() => setShowSpeedDropdown(!showSpeedDropdown)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl border border-vibrant-orange/30
                         bg-white/90 hover:bg-vibrant-orange/5 transition-all duration-200
                         text-surface-primary font-semibold text-sm shadow-[0_10px_20px_rgba(255,138,76,0.12)]
                         ${isRTL ? 'flex-row-reverse' : ''}`}
            >
              <span>{playbackRate}x</span>
              <ChevronDown className={`w-4 h-4 text-vibrant-orange transition-transform duration-200 ${showSpeedDropdown ? 'rotate-180' : ''} ${isRTL ? 'order-first' : ''}`} />
            </button>

            {showSpeedDropdown && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowSpeedDropdown(false)}
                />
                <div className={`absolute ${isRTL ? 'left-0' : 'right-0'} mt-2 w-24 bg-white border border-vibrant-orange/25 rounded-xl shadow-[0_20px_35px_rgba(37,13,73,0.15)] z-20 overflow-hidden`}>
                  {speedOptions.map((speed) => (
                    <button
                      key={speed}
                      onClick={() => changeSpeed(speed)}
                      className={`w-full px-4 py-2.5 text-sm font-medium text-left transition-all duration-150
                                ${playbackRate === speed
                                  ? 'bg-vibrant-orange text-white'
                                  : 'text-surface-primary hover:bg-vibrant-orange/10'
                                }
                                ${isRTL ? 'text-right' : 'text-left'}
                              `}
                    >
                      {speed}x
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

interface MaterialsStepProps {
  session: StudySession;
  onSessionUpdate: (session: StudySession) => void;
  onStepChange: (step: FlowStep) => void;
  isRTL: boolean;
}

const MaterialsStep: React.FC<MaterialsStepProps> = ({
  session,
  onSessionUpdate,
  onStepChange,
  isRTL
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [generating, setGenerating] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [learningFocus, setLearningFocus] = useState('');
  const [showFocusModal, setShowFocusModal] = useState(false);
  const [modalLearningFocus, setModalLearningFocus] = useState('');
  const [showRegenerateFocusModal, setShowRegenerateFocusModal] = useState(false);
  const [regenerateFocus, setRegenerateFocus] = useState('');
  const [selectedDocumentsToDelete, setSelectedDocumentsToDelete] = useState<number[]>([]);
  const [selectedTranscriptionsToDelete, setSelectedTranscriptionsToDelete] = useState<number[]>([]);
  const [podcastData, setPodcastData] = useState<PodcastResponse | null>(null);
  const [podcastLoading, setPodcastLoading] = useState(false);
  const [isFetchingPodcast, setIsFetchingPodcast] = useState(false);
  const [podcastError, setPodcastError] = useState<string | null>(null);
  const buildShareUrl = useCallback((token?: string | null) => {
    if (!token) return null;
    return `${window.location.origin}/share/study/${token}`;
  }, []);
  const [shareUrl, setShareUrl] = useState<string | null>(buildShareUrl(session?.share_token));
  const [shareLoading, setShareLoading] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);

  // VIP Status
  const [isVip, setIsVip] = useState(false);
  const [pdfExportError, setPdfExportError] = useState<string | null>(null);
  
  // Floating back button
  const [showFloatingBack, setShowFloatingBack] = useState(false);

  useEffect(() => {
    setShareUrl(buildShareUrl(session?.share_token));
  }, [session?.share_token, buildShareUrl]);

  const detailedSummaryMaterial = session?.study_materials?.find(material => material.material_type === 'detailed_summary');
  const hasDetailedSummary = () => Boolean(detailedSummaryMaterial);
  const hasStudyMaterials = () => hasDetailedSummary();
  const hasCompletedTranscription = () => Boolean(session?.transcriptions?.some(t => t.status === 'completed'));
  const hasDocuments = () => Boolean(session?.documents && session.documents.length > 0);
  const hasAnyContent = () => hasCompletedTranscription() || hasDocuments();

  const fetchPodcast = useCallback(async (withLoader: boolean = true) => {
    if (!isVip || !session?.id) return;
    if (withLoader) {
      setIsFetchingPodcast(true);
    }

    try {
      const response = await aiApi.getPodcast(session.id);
      setPodcastData(response);
      setPodcastError(null);
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 404) {
        setPodcastData(null);
        setPodcastError(null);
      } else {
        console.error('Error loading podcast:', error);
        setPodcastError(t('sessions:flow.materials.podcast.fetchError'));
      }
    } finally {
      setIsFetchingPodcast(false);
    }
  }, [session?.id, isVip, t]);

  useEffect(() => {
    if (session?.learning_focus) {
      setLearningFocus(session.learning_focus);
    }
    
    // Load user data to check VIP status
    loadUserData();
    
    // Restore scroll position if exists
    if (session?.id) {
      const key = `studySession_${session.id}_scrollY`;
      const savedScrollY = localStorage.getItem(key);
      if (savedScrollY && savedScrollY !== '0') {
        setTimeout(() => {
          window.scrollTo(0, parseInt(savedScrollY));
        }, 500); // Delay to ensure content is loaded
      }
    }
  }, [session]);

  useEffect(() => {
    fetchPodcast();
  }, [fetchPodcast]);
  
  // Save scroll position when leaving
  useEffect(() => {
    let scrollTimeout: NodeJS.Timeout;
    
    const saveScrollPosition = () => {
      if (session?.id && window.scrollY > 0) {  // Only save if scrolled
        localStorage.setItem(`studySession_${session.id}_scrollY`, window.scrollY.toString());
      }
    };
    
    // Throttled scroll handler
    const handleScroll = () => {
      // Show floating button when scrolled past 200px AND not near bottom
      const scrolledPastThreshold = window.scrollY > 200;
      const nearBottom = window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 300;
      setShowFloatingBack(scrolledPastThreshold && !nearBottom);

      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        saveScrollPosition();
      }, 100); // Save 100ms after scrolling stops
    };
    
    window.addEventListener('scroll', handleScroll);
    
    // Save on unmount
    return () => {
      clearTimeout(scrollTimeout);
      saveScrollPosition();
      window.removeEventListener('scroll', handleScroll);
    };
  }, [session?.id]);
  
  const loadUserData = async () => {
    try {
      const userData = await authApi.getCurrentUser();
      setIsVip(userData.is_vip);
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  // Removed auto-generation of materials to allow users to upload multiple files
  // Users can manually generate materials when ready

  const handleGeneratePodcast = async () => {
    if (!isVip || !session?.id) return;

    if (!hasDetailedSummary()) {
      setPodcastError(t('sessions:flow.materials.podcast.summaryRequired'));
      return;
    }

    setPodcastLoading(true);
    setPodcastError(null);

    try {
      const request = {
        session_id: session.id,
        voice_id: PODCAST_DEFAULT_VOICE_ID,
      };

      const response = await aiApi.generatePodcast(request);
      setPodcastData(response);

      const updatedSession = await studySessionsApi.getSession(session.id);
      onSessionUpdate(updatedSession);

      await fetchPodcast(false);
    } catch (error: any) {
      console.error('Error generating podcast:', error);
      const detail = error?.response?.data?.detail;
      setPodcastError(detail || t('sessions:flow.materials.podcast.generationError'));
    } finally {
      setPodcastLoading(false);
    }
  };

  const renderPodcastSection = () => {
    if (!isVip || !detailedSummaryMaterial) {
      return null;
    }

    const summaryReady = hasDetailedSummary();
    const isBusy = podcastLoading || isFetchingPodcast;
    const showPlayer = Boolean(podcastData?.audio_url);
    const buttonLabel = podcastData
      ? t('sessions:flow.materials.podcast.regenerateButton')
      : t('sessions:flow.materials.podcast.createButton');
    const bodyClasses = showPlayer
      ? 'pb-8'
      : 'py-8 sm:py-10 flex flex-col items-center text-center gap-5';

    return (
      <SpaceCard className="mb-6 border-2 border-vibrant-orange/40 bg-white/95 overflow-visible">
        <div className="flex flex-col gap-4 w-full">
          {showPlayer && (
            <div className={`flex items-center ${isRTL ? 'flex-row-reverse space-x-reverse space-x-3' : 'space-x-3'} px-6 sm:px-8 pt-6 sm:pt-8`}>
            <div className="w-12 h-12 bg-white border-2 border-vibrant-orange rounded-full flex items-center justify-center flex-shrink-0 shadow-[0_8px_18px_rgba(255,138,76,0.2)]">
              <img
                src="/astronaut_podcast.png"
                alt={t('sessions:flow.materials.podcast.buttonIconAlt')}
                className="w-9 h-9 object-contain"
              />
            </div>
            <h2 className={`text-2xl font-bold text-vibrant-orange flex-1 min-w-0 ${isRTL ? 'text-right pr-3' : ''}`}>
              <MixedText>{t('sessions:flow.materials.podcast.title')}</MixedText>
            </h2>
          </div>

          )}

          <div className={`${bodyClasses} px-6 sm:px-8`}>
            {!showPlayer && (
              <div className="flex justify-center">
                <Button
                  onClick={handleGeneratePodcast}
                  variant="gradient"
                  size="xl"
                  className="!rounded-full !px-10 sm:!px-14 !py-5 sm:!py-6 text-lg shadow-vibrant-orange/25"
                  loading={podcastLoading || isFetchingPodcast}
                  disabled={isBusy || !summaryReady}
                >
                  <span className="flex items-center gap-4 sm:gap-6">
                    <img
                      src="/astronaut_podcast.png"
                      alt={t('sessions:flow.materials.podcast.buttonIconAlt')}
                      className="w-16 h-16 sm:w-20 sm:h-20 object-contain drop-shadow-[0_4px_10px_rgba(255,138,76,0.4)]"
                    />
                    <span className="text-2xl font-semibold">
                      <MixedText>{buttonLabel}</MixedText>
                    </span>
                  </span>
                </Button>
              </div>
            )}

            {podcastError && (
              <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                <MixedText>{podcastError}</MixedText>
              </div>
            )}

            {showPlayer && podcastData?.audio_url && (
              <div className="mt-4">
                <CustomPodcastPlayer audioUrl={podcastData.audio_url} isRTL={isRTL} />
              </div>
            )}
          </div>
        </div>
      </SpaceCard>
    );
  };

  const handleGenerateMaterials = async () => {
    setGenerating(true);
    try {
      const request = {
        session_id: session.id,
        material_types: ['detailed_summary']
      };
      
      const response = await aiApi.generateMaterials(request);
      
      // Refresh session
      const updatedSession = await studySessionsApi.getSession(session.id);
      onSessionUpdate(updatedSession);
      
    } catch (error) {
      console.error('Error generating materials:', error);
    } finally {
      setGenerating(false);
    }
  };

  const handleRegenerateMaterials = async () => {
    setRegenerating(true);
    try {
      const request = {
        session_id: session.id,
        material_types: ['detailed_summary', 'short_lesson_timeline']
      };
      
      const response = await aiApi.generateMaterials(request);
      
      // Refresh session
      const updatedSession = await studySessionsApi.getSession(session.id);
      onSessionUpdate(updatedSession);
    } catch (error) {
      console.error('Error regenerating materials:', error);
    } finally {
      setRegenerating(false);
    }
  };

  const handleUpdateLearningFocus = async () => {
    if (modalLearningFocus.trim() === learningFocus) {
      setShowFocusModal(false);
      return;
    }

    try {
      const updatedSession = await studySessionsApi.updateSession(session.id, {
        learning_focus: modalLearningFocus.trim() || undefined
      });
      onSessionUpdate(updatedSession);
      setLearningFocus(modalLearningFocus.trim());
      setShowFocusModal(false);
    } catch (error) {
      console.error('Error updating learning focus:', error);
    }
  };

  const openFocusModal = () => {
    setModalLearningFocus(learningFocus);
    setShowFocusModal(true);
  };

  const openRegenerateFocusModal = () => {
    setRegenerateFocus(learningFocus);
    setSelectedDocumentsToDelete([]);
    setSelectedTranscriptionsToDelete([]);
    setShowRegenerateFocusModal(true);
  };
  
  const handleExportPdf = () => {
    if (!session || !isVip) {
      setPdfExportError(t('sessions:pdfExport.errors.vipRequired'));
      return;
    }

    // Navigate to PDF export view for study session
    navigate(`/study-session/${session.id}/pdf-export`);
  };

  const handleShareMaterials = async () => {
    if (!session) return;
    setShareError(null);
    setShareLoading(true);
    try {
      let token = session.share_token;
      if (!session.is_shared) {
        const response = await shareApi.enableStudyShare(session.id);
        token = response.share_token || token;
        onSessionUpdate({ ...session, is_shared: true, share_token: token });
        setShareUrl(response.share_url || buildShareUrl(token));
      } else if (!shareUrl && token) {
        setShareUrl(buildShareUrl(token));
      }

      const linkToCopy = shareUrl || buildShareUrl(token);
      if (linkToCopy) {
        await navigator.clipboard.writeText(linkToCopy);
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 1500);
      }
    } catch (error) {
      console.error('Failed to share study materials', error);
      setShareError(t('sessions:share.error', 'Unable to create a share link right now. Please try again.'));
    } finally {
      setShareLoading(false);
    }
  };

  const handleDisableShare = async () => {
    if (!session?.is_shared) return;
    setShareLoading(true);
    setShareError(null);
    try {
      await shareApi.disableStudyShare(session.id);
      onSessionUpdate({ ...session, is_shared: false });
      setShareUrl(null);
    } catch (error) {
      console.error('Failed to disable share', error);
      setShareError(t('sessions:share.disableError', 'Unable to disable sharing. Please try again.'));
    } finally {
      setShareLoading(false);
    }
  };

  const toggleDocumentSelection = (documentId: number) => {
    setSelectedDocumentsToDelete(prev => 
      prev.includes(documentId) 
        ? prev.filter(id => id !== documentId)
        : [...prev, documentId]
    );
  };

  const toggleTranscriptionSelection = (transcriptionId: number) => {
    setSelectedTranscriptionsToDelete(prev => 
      prev.includes(transcriptionId) 
        ? prev.filter(id => id !== transcriptionId)
        : [...prev, transcriptionId]
    );
  };

  const deleteSelectedContent = async () => {
    try {
      // Delete selected documents
      for (const documentId of selectedDocumentsToDelete) {
        await documentsApi.deleteDocument(documentId);
      }
      
      // Delete selected transcriptions
      for (const transcriptionId of selectedTranscriptionsToDelete) {
        await transcriptionApi.deleteTranscription(transcriptionId);
      }
      
      // Refresh session
      const updatedSession = await studySessionsApi.getSession(session.id);
      onSessionUpdate(updatedSession);
    } catch (error) {
      console.error('Error deleting selected content:', error);
    }
  };

  const handleRegenerateWithFocus = async () => {
    setRegenerating(true);
    setShowRegenerateFocusModal(false);
    
    try {
      // Delete selected content if any
      if (selectedDocumentsToDelete.length > 0 || selectedTranscriptionsToDelete.length > 0) {
        await deleteSelectedContent();
      }
      
      // Update learning focus if changed
      if (regenerateFocus.trim() !== learningFocus) {
        await studySessionsApi.updateSession(session.id, {
          learning_focus: regenerateFocus.trim() || undefined
        });
        setLearningFocus(regenerateFocus.trim());
      }
      
      // Regenerate materials
      const request = {
        session_id: session.id,
        material_types: ['detailed_summary', 'short_lesson_timeline']
      };
      
      const response = await aiApi.generateMaterials(request);
      
      // Refresh session
      const updatedSession = await studySessionsApi.getSession(session.id);
      onSessionUpdate(updatedSession);
    } catch (error) {
      console.error('Error regenerating materials with focus:', error);
    } finally {
      setRegenerating(false);
    }
  };

  const renderActionButtons = () => (
    <div className={`flex flex-col-reverse sm:flex-row justify-center items-center gap-4 w-full ${isRTL ? 'flex-row-reverse' : ''}`}>
      <Button
        onClick={() => onStepChange('upload')}
        variant="ghost"
        icon={isRTL ? <ArrowRight className="w-4 h-4" /> : <ArrowLeft className="w-4 h-4" />}
        iconPosition="left"
        className="w-full sm:w-auto justify-center"
      >
        {t('sessions:flow.materials.backToUpload')}
      </Button>

      <div className={`flex flex-wrap items-center justify-center gap-3 sm:gap-4 w-full sm:w-auto ${isRTL ? 'flex-row-reverse' : ''}`}>
        {/* Share Button */}
        {hasStudyMaterials() && (
          <div className={`flex items-center gap-2 w-full sm:w-auto justify-center ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Button
              onClick={handleShareMaterials}
              variant={session.is_shared ? 'secondary' : 'primary'}
              icon={<Share2 className="w-4 h-4" />}
              iconPosition="left"
              loading={shareLoading}
              className="w-full sm:w-auto justify-center"
            >
              <MixedText>
                {shareCopied
                  ? t('sessions:share.copied', 'Link copied!')
                  : session.is_shared
                  ? t('sessions:share.copy', 'Copy share link')
                  : t('sessions:share.enable', 'Share read-only link')}
              </MixedText>
            </Button>
            {/* Disable Share Button - Hidden for now
            {session.is_shared && (
              <button
                onClick={handleDisableShare}
                disabled={shareLoading}
                className="text-xs text-surface-secondary hover:text-red-500 underline underline-offset-4 transition-colors"
              >
                {t('sessions:share.disable', 'Disable')}
              </button>
            )}
            */}
          </div>
        )}

        {/* PDF Export Button - VIP Only */}
        {isVip && hasStudyMaterials() && (
          <Button
            onClick={handleExportPdf}
            variant="secondary"
            icon={<Download className="w-4 h-4" />}
            iconPosition="left"
            className="w-full sm:w-auto justify-center"
          >
            <MixedText>{t('sessions:pdfExport.button')}</MixedText>
          </Button>
        )}
        
        {/* Regenerate Materials Button - Only show when materials exist */}
        {hasStudyMaterials() && !generating && (
          <Button
            onClick={openRegenerateFocusModal}
            disabled={regenerating}
            variant="secondary"
            icon={<RefreshCw className="w-4 h-4" />}
            iconPosition="left"
            loading={regenerating}
            className="w-full sm:w-auto justify-center"
          >
            {t('sessions:flow.materials.regenerateMaterials')}
          </Button>
        )}

        {/* Continue Button */}
        <Button
          onClick={() => onStepChange('questions')}
          variant="primary"
          icon={isRTL ? <ArrowLeft className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
          iconPosition={isRTL ? "left" : "right"}
          className="w-full sm:w-auto justify-center"
        >
          {t('sessions:flow.materials.continueToPractice')}
        </Button>
      </div>
    </div>
  );

  if (generating) {
    return (
      <div className="space-y-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-bg-primary mb-4">
            {t('sessions:flow.materials.title', 'Study Materials')}
          </h2>
          <p className="text-bg-secondary max-w-2xl mx-auto">
            {t('sessions:flow.materials.description', 'AI-generated study materials from your content')}
          </p>
        </div>

        <SpaceCard className="text-center py-16">
          <div className="w-16 h-16 bg-gradient-to-r from-vibrant-orange to-vibrant-orange/90 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
            <img src="/astronaut_reading.png" alt="Astronaut Reading" className="w-8 h-8" />
          </div>
          <h3 className="text-xl font-semibold text-surface-primary mb-2">
            <MixedText>{t('sessions:flow.materials.generatingMaterials', 'Generating Study Materials...')}</MixedText>
          </h3>
          <p className="text-surface-secondary max-w-md mx-auto">
            <MixedText>{t('sessions:flow.materials.analyzingContent', 'Our AI is analyzing your content to create personalized study materials')}</MixedText>
          </p>
          <div className="mt-6">
            <div className="flex items-center justify-center space-x-2">
              <div className="w-3 h-3 bg-vibrant-orange rounded-full animate-bounce"></div>
              <div className="w-3 h-3 bg-vibrant-orange rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
              <div className="w-3 h-3 bg-vibrant-orange rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            </div>
          </div>
        </SpaceCard>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-bg-primary mb-4">
          🌌 {t('sessions:flow.materials.title', 'Study Materials')}
        </h2>
        <p className="text-bg-secondary max-w-2xl mx-auto">
          {t('sessions:flow.materials.description', 'AI-generated study materials from your content')}
        </p>
      </div>



      {hasStudyMaterials() ? (
        <div className="space-y-6">
          {renderActionButtons()}
          
          {/* renderPodcastSection() */}

          {detailedSummaryMaterial && (
            <SpaceCard className="border-2 border-vibrant-orange/30 bg-gradient-to-br from-white to-vibrant-orange/5">
              <div className="p-8">
                <div className={`flex items-center mb-6 ${isRTL ? 'flex-row-reverse space-x-reverse space-x-3' : 'space-x-3'}`}>
                  <div className="w-10 h-10 bg-vibrant-orange rounded-lg flex items-center justify-center flex-shrink-0">
                    <FileText className="w-5 h-5 text-white" />
                  </div>
                  <h2 className={`text-2xl font-bold text-vibrant-orange flex-1 min-w-0 ${isRTL ? 'text-right pr-3' : ''}`}>
                    {isRTL ? 'סיכום השיעור' : 'Lesson Summary'}
                  </h2>
                </div>

                <StudyMaterialRenderer
                  content={detailedSummaryMaterial.content}
                  materialType={detailedSummaryMaterial.material_type}
                  isRTL={isRTL}
                />
              </div>
            </SpaceCard>
          )}

          {/* Navigation */}
          {renderActionButtons()}
          
          {/* PDF Export Error Message */}
          {pdfExportError && (
            <div className={`mt-3 text-center text-xs text-red-500 font-hebrew`}>
              <MixedText>{pdfExportError}</MixedText>
            </div>
          )}
          {shareError && (
            <div className="mt-2 text-center text-xs text-red-500 font-hebrew">
              <MixedText>{shareError}</MixedText>
            </div>
          )}
        </div>
      ) : (
        <SpaceCard className="text-center py-20">
          <div className="w-20 h-20 bg-gradient-to-br from-vibrant-orange/20 to-vibrant-orange/10 rounded-2xl flex items-center justify-center mx-auto mb-8">
            <img src="/astronaut_reading.png" alt="Astronaut Reading" className="w-10 h-10" />
          </div>
          <h3 className="text-2xl font-bold text-surface-primary mb-3 text-center">
            <MixedText>{t('sessions:flow.materials.noMaterials', 'No Study Materials Yet')}</MixedText>
          </h3>
          <p className="text-surface-secondary max-w-lg mx-auto mb-8 text-lg leading-relaxed text-center">
            <MixedText>
              {hasAnyContent() 
                ? t('sessions:flow.materials.readyToGenerate', 'Your content is ready. Generate AI study materials to continue.')
                : t('sessions:flow.materials.noMaterialsDescription', 'Upload some content first to generate study materials')
              }
            </MixedText>
          </p>
          <div className="flex justify-center">
            {hasAnyContent() ? (
              <Button
                onClick={handleGenerateMaterials}
                disabled={generating}
                variant="primary"
                className="px-12 py-4 text-lg font-semibold bg-gradient-to-r from-vibrant-orange to-vibrant-orange/90 hover:from-vibrant-orange/90 hover:to-vibrant-orange/80 shadow-lg shadow-vibrant-orange/30 transition-all duration-200 hover:scale-105 flex items-center justify-center min-w-[280px]"
              >
                {generating ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                    <span><MixedText>{t('sessions:flow.materials.generating')}</MixedText></span>
                  </>
                ) : (
                  <span><MixedText>{t('sessions:flow.materials.generateMaterials', 'צור חומרי למידה')}</MixedText></span>
                )}
              </Button>
            ) : (
              <Button
                onClick={() => onStepChange('upload')}
                variant="primary"
                className="px-12 py-4 text-lg font-semibold bg-gradient-to-r from-vibrant-orange to-vibrant-orange/90 hover:from-vibrant-orange/90 hover:to-vibrant-orange/80 shadow-lg shadow-vibrant-orange/30 transition-all duration-200 hover:scale-105 min-w-[280px]"
              >
                <MixedText>{t('sessions:flow.materials.goToUpload', 'Go to Upload')}</MixedText>
              </Button>
            )}
          </div>
        </SpaceCard>
      )}

      {/* Learning Focus Modal */}
      {showFocusModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <SpaceCard className="w-full max-w-md shadow-2xl shadow-vibrant-orange/20">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-surface-primary mb-4">
                🎯 {t('sessions:flow.materials.setLearningFocus', 'Set Learning Focus')}
              </h3>
              <p className="text-surface-secondary text-sm mb-4">
                {t('sessions:flow.materials.focusDescription', 'What specific topics or skills would you like to focus on?')}
              </p>
              <textarea
                value={modalLearningFocus}
                onChange={(e) => setModalLearningFocus(e.target.value)}
                placeholder={t('sessions:flow.materials.focusPlaceholder', 'e.g., Focus on problem-solving techniques and mathematical proofs')}
                className="w-full h-24 p-3 border border-surface-muted/30 rounded-lg bg-white/50 dark:bg-deep-space-purple/20 text-surface-primary placeholder-surface-muted resize-none focus:outline-none focus:ring-2 focus:ring-vibrant-orange/50"
                dir={isRTL ? 'rtl' : 'ltr'}
              />
              <div className="flex flex-col gap-3 mt-4">
                <Button
                  onClick={handleUpdateLearningFocus}
                  variant="primary"
                  className="w-full"
                >
                  {t('common:actions.save', 'Save')}
                </Button>
                <Button
                  onClick={() => setShowFocusModal(false)}
                  variant="ghost"
                  className="w-full"
                >
                  {t('common:actions.cancel', 'Cancel')}
                </Button>
              </div>
            </div>
          </SpaceCard>
        </div>
      )}

      {/* Regenerate with Focus Modal */}
      {showRegenerateFocusModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <SpaceCard className="w-full max-w-lg shadow-2xl shadow-vibrant-orange/20 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className={`flex items-center mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className={`w-10 h-10 bg-gradient-to-br from-vibrant-orange to-vibrant-orange/90 rounded-xl flex items-center justify-center ${isRTL ? 'ml-3' : 'mr-3'}`}>
                  <RefreshCw className="w-5 h-5 text-white" />
                </div>
                <h3 className={`text-lg font-semibold text-surface-primary ${isRTL ? 'text-right' : ''}`}>
                  <MixedText>{t('sessions:flow.materials.regenerateTitle', isRTL ? 'יצירת חומרים מחדש' : 'Regenerate Materials')}</MixedText>
                </h3>
              </div>
              
              <p className={`text-surface-secondary text-sm mb-4 ${isRTL ? 'text-right' : ''}`}>
                <MixedText>{t('sessions:flow.materials.regenerateDescription', isRTL 
                  ? 'על מה תרצה להתמקד בחומרים החדשים? (אופציונלי)'
                  : 'What would you like to focus on in the new materials? (Optional)'
                )}
                </MixedText>
              </p>
              
              <textarea
                value={regenerateFocus}
                onChange={(e) => setRegenerateFocus(e.target.value)}
                placeholder={isRTL 
                  ? 'לדוגמה: התמקדות על פתרון בעיות וטכניקות מתמטיות'
                  : 'e.g., Focus on problem-solving techniques and mathematical proofs'
                }
                className="w-full h-20 p-3 border border-surface-muted/30 rounded-lg bg-white/50 dark:bg-deep-space-purple/20 text-surface-primary placeholder-surface-muted resize-none focus:outline-none focus:ring-2 focus:ring-vibrant-orange/50 transition-all duration-200"
                dir={isRTL ? 'rtl' : 'ltr'}
              />

              {/* Content Selection Options */}
              <div className="mt-6">
                <div className={`mb-4 ${isRTL ? 'text-right' : ''}`}>
                  <h4 className={`text-sm font-medium text-surface-primary ${isRTL ? 'text-right' : ''}`}>
                   <MixedText>{t('sessions:flow.materials.selectContentToDelete', 'Select content to delete:')}</MixedText>
                  </h4>
                  <p className={`text-xs text-surface-muted mt-1 ${isRTL ? 'text-right' : ''}`}>
                    <MixedText>{t('sessions:flow.materials.selectContentOptional', 'Optional: Choose content to delete before generating new materials')}</MixedText>
                  </p>
                </div>
                
                                                  {((session?.transcriptions && session.transcriptions.length > 0) || (session?.documents && session.documents.length > 0)) && (
                   <div className="space-y-4">
                     
                     {/* Audio/Video Lectures */}
                     {session?.transcriptions && session.transcriptions.length > 0 && (
                       <div>
                         <h4 className={`text-sm font-semibold text-surface-primary mb-3 flex items-center ${isRTL ? 'flex-row-reverse text-right' : 'space-x-2'}`}>
                           <Play className="w-4 h-4 text-vibrant-orange" />
                           <span className={`${isRTL ? 'mr-2' : 'ml-2'}`}>
                             {t('sessions:flow.materials.audioLectures', 'Audio/Video Lectures')}
                           </span>
                         </h4>
                         <div className="grid grid-cols-1 gap-2">
                           {session.transcriptions.map((transcription) => {
                             const isSelected = selectedTranscriptionsToDelete.includes(transcription.id);
                             return (
                               <div
                                 key={transcription.id}
                                 className={`
                                   relative p-3 rounded-lg border-2 transition-all duration-200
                                   ${isSelected
                                     ? 'border-red-500/50 bg-red-50/50 dark:bg-red-900/10'
                                     : 'border-surface-muted/30 bg-white/50 dark:bg-deep-space-purple/10'
                                   }
                                 `}
                               >
                                 <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                                   <div className={`flex items-center flex-1 min-w-0 ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
                                     <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-vibrant-orange to-vibrant-orange/90 flex items-center justify-center">
                                       <Play className="w-4 h-4 text-white" />
                                     </div>
                                     <div className={`flex-1 min-w-0 ${isRTL ? 'text-right pr-2' : ''}`}>
                                       <p className={`text-sm font-medium truncate transition-all duration-200 ${
                                         isSelected 
                                           ? 'text-red-700 dark:text-red-400 line-through' 
                                           : 'text-surface-primary'
                                       }`}>
                                         {transcription.original_filename || 'Audio File'}
                                       </p>
                                       <p className={`text-xs truncate transition-all duration-200 ${
                                         isSelected 
                                           ? 'text-red-600/80 dark:text-red-400/80' 
                                           : 'text-surface-muted'
                                       }`}>
                                         {isSelected ? 
                                           t('sessions:flow.materials.willBeDeletedDuringRegeneration', 'Will be deleted during regeneration') :
                                           (isRTL ? 'קובץ אודיו/וידאו' : 'Audio/Video file')
                                         }
                                       </p>
                                     </div>
                                   </div>
                                   
                                   {/* Delete button */}
                                   <button
                                     onClick={() => toggleTranscriptionSelection(transcription.id)}
                                     className={`p-2 rounded-lg transition-all duration-200 hover:scale-110 ${
                                       isSelected
                                         ? 'bg-red-500 hover:bg-red-600 text-white'
                                         : 'bg-red-500/10 hover:bg-red-500/20 text-red-500 hover:text-red-600'
                                     }`}
                                     title={isSelected 
                                       ? t('common:actions.cancel', 'Cancel deletion') 
                                       : t('sessions:flow.upload.deleteTranscription', 'Delete transcription')
                                     }
                                   >
                                     <Trash2 className="w-4 h-4" />
                                   </button>
                                 </div>
                               </div>
                             );
                           })}
                         </div>
                       </div>
                     )}
                     
                     {/* Documents */}
                     {session?.documents && session.documents.length > 0 && (
                       <div>
                         <h4 className={`text-sm font-semibold text-surface-primary mb-3 flex items-center ${isRTL ? 'flex-row-reverse text-right' : 'space-x-2'}`}>
                           <FileText className="w-4 h-4 text-vibrant-orange" />
                           <span className={`${isRTL ? 'mr-2' : 'ml-2'}`}>
                             <MixedText>{t('sessions:flow.materials.documents', 'Documents')}</MixedText>
                           </span>
                         </h4>
                         <div className="grid grid-cols-1 gap-2">
                           {session.documents.map((document) => {
                             const isSelected = selectedDocumentsToDelete.includes(document.id);
                             return (
                               <div
                                 key={document.id}
                                 className={`
                                   relative p-3 rounded-lg border-2 transition-all duration-200
                                   ${isSelected
                                     ? 'border-red-500/50 bg-red-50/50 dark:bg-red-900/10'
                                     : 'border-surface-muted/30 bg-white/50 dark:bg-deep-space-purple/10'
                                   }
                                 `}
                               >
                                 <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                                   <div className={`flex items-center flex-1 min-w-0 ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
                                     <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-vibrant-orange to-vibrant-orange/90 flex items-center justify-center">
                                       <FileText className="w-4 h-4 text-white" />
                                     </div>
                                     <div className={`flex-1 min-w-0 ${isRTL ? 'text-right pr-2' : ''}`}>
                                       <p className={`text-sm font-medium truncate transition-all duration-200 ${
                                         isSelected 
                                           ? 'text-red-700 dark:text-red-400 line-through' 
                                           : 'text-surface-primary'
                                       }`}>
                                         {document.original_filename || 'Document'}
                                       </p>
                                       <p className={`text-xs truncate transition-all duration-200 ${
                                         isSelected 
                                           ? 'text-red-600/80 dark:text-red-400/80' 
                                           : 'text-surface-muted'
                                       }`}>
                                         {isSelected ? 
                                           t('sessions:flow.materials.willBeDeletedDuringRegeneration', 'Will be deleted during regeneration') :
                                           document.file_type
                                         }
                                       </p>
                                     </div>
                                   </div>
                                   
                                   {/* Delete button */}
                                   <button
                                     onClick={() => toggleDocumentSelection(document.id)}
                                     className={`p-2 rounded-lg transition-all duration-200 hover:scale-110 mr-3 ml-3 ${
                                       isSelected
                                         ? 'bg-red-500 hover:bg-red-600 text-white'
                                         : 'bg-red-500/10 hover:bg-red-500/20 text-red-500 hover:text-red-600'
                                     }`}
                                     title={isSelected 
                                       ? t('common:actions.cancel', 'Cancel deletion') 
                                       : t('sessions:flow.upload.deleteDocument', 'Delete document')
                                     }
                                   >
                                     <Trash2 className="w-4 h-4" />
                                   </button>
                                 </div>
                               </div>
                             );
                           })}
                         </div>
                       </div>
                     )}
                   </div>
                 )}
              </div>

              <div className="flex flex-col gap-3 mt-6">
                <Button
                  onClick={handleRegenerateWithFocus}
                  variant="primary"
                  disabled={regenerating}
                  className="w-full bg-gradient-to-r from-vibrant-orange to-vibrant-orange/90 hover:from-vibrant-orange/90 hover:to-vibrant-orange/80 shadow-lg shadow-vibrant-orange/30"
                >
                  {regenerating ? (
                    <>
                      <div className={`animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent ${isRTL ? 'ml-2' : 'mr-2'}`}></div>
                      {t('sessions:flow.materials.regenerating', isRTL ? 'מייצר...' : 'Regenerating...')}
                    </>
                  ) : (
                    <>
                       {(selectedDocumentsToDelete.length > 0 || selectedTranscriptionsToDelete.length > 0) 
                         ? t('sessions:flow.materials.deleteSelectedContent', isRTL ? 'מחק תוכן נבחר ויצור חומרים מחדש' : 'Delete selected content and regenerate materials')
                         : t('sessions:flow.materials.regenerateMaterials', isRTL ? 'יצירה מחדש' : 'Regenerate')
                       }
                    </>
                  )}
                </Button>
                <Button
                  onClick={() => setShowRegenerateFocusModal(false)}
                  variant="ghost"
                  className="w-full hover:bg-surface-muted/10"
                >
                  {t('common:actions.cancel', 'Cancel')}
                </Button>
              </div>
            </div>
          </SpaceCard>
        </div>
      )}
      
      {/* Floating Back to Course Button */}
      {showFloatingBack && (
        <button
          onClick={() => navigate(`/course/${session?.course_id}`)}
          className={`hidden md:block fixed left-6 bottom-6 z-50
            bg-vibrant-orange text-white p-4 rounded-full shadow-lg
            hover:bg-vibrant-orange/90 transition-all duration-300
            hover:scale-110 transform hover:-translate-y-1`}
          title={isRTL ? 'חזרה לקורס' : 'Back to Course'}
        >
                      <MixedText>{t('common:navigation.back_to_course', { courseName: session?.course?.name || t('common:labels.course') })}</MixedText>
        </button>
      )}
    </div>
  );
};

export default MaterialsStep; 
